import { useState, useEffect } from "react";
import {
  Typography,
  Container,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  CheckCircle,
  HourglassEmpty,
  Delete,
  Add,
  Edit,
  RocketLaunch,
  ContentCopy,
  Psychology,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";

import LoaderOverlay from "../../components/LoaderOverlay";
import { showMessage } from "../../customhooks/useNotifier";
import knowledgebaseAgentService from "../../services/knowledgebase-agent.service";

// ----------------- KBCard Component -----------------
const KBCard = ({ kb, status, onEdit, onDelete, setLoading, loadKBs }) => {
  const [copying, setCopying] = useState(false);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userPlan = user?.plan || "free";

  const handleCopyWidgetCode = async (e) => {
    e.stopPropagation();
    if (!kb.id) return;

    try {
      setCopying(true);

      const agentData = await knowledgebaseAgentService.getKnowledgeBaseById(
        kb.id,
      );

      if (!agentData || agentData.crawlStatus !== "completed") {
        alert(
          "Widget not ready. Ensure crawling is completed before copying the code.",
        );
        return;
      }

      const code = `
      <script src="https://onboarding.sitepilot.online/widget.js" defer></script>
      <script>
        window.addEventListener("load", function () {
          if (typeof window.initChatWidget === "function") {          
            window.initChatWidget({ kbId: "${kb.id}" });
          } else {
            console.error("Chat widget not loaded.");
          }
        });
      </script>`.trim();

      await navigator.clipboard.writeText(code);
      showMessage("Widget code copied to clipboard!", "success");
    } catch (err) {
      console.error(err);
      showMessage("Failed to copy widget code.", "error");
    } finally {
      setCopying(false);
    }
  };

  return (
    <Grid item xs={12} md={6}>
      <Card
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          border:
            status.color === "success"
              ? "2px solid #10B981"
              : "1px solid rgba(226, 232, 240, 0.8)",
          borderRadius: 4,
          background:
            "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%)",
          backdropFilter: "blur(20px)",
          boxShadow:
            status.color === "success"
              ? "0 20px 40px -12px rgba(16, 185, 129, 0.15), 0 0 20px rgba(16, 185, 129, 0.1)"
              : "0 20px 40px -12px rgba(0, 0, 0, 0.15)",
          position: "relative",
          overflow: "hidden",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          "&::before":
            status.color === "success"
              ? {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background:
                    "linear-gradient(90deg, #10B981 0%, #059669 100%)",
                  borderRadius: "16px 16px 0 0",
                }
              : {},
          "&:hover": {
            transform: "translateY(-6px) scale(1.01)",
            boxShadow:
              status.color === "success"
                ? "0 28px 56px -12px rgba(16, 185, 129, 0.25), 0 0 30px rgba(16, 185, 129, 0.2)"
                : "0 28px 56px -12px rgba(0, 0, 0, 0.25)",
            borderColor:
              status.color === "success"
                ? "#10B981"
                : "rgba(102, 126, 234, 0.4)",
          },
        }}
      >
        <CardContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            p: 3,
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {kb.knowledgeName || "Untitled KB"}
              </Typography>
              {kb.crawlStatus !== "queued" && (
                <Chip
                  icon={status.icon}
                  label={status.label}
                  color={status.color}
                  size="small"
                  sx={{
                    mt: 1,
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    height: "28px",
                    background:
                      status.color === "success"
                        ? "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)"
                        : status.color === "info"
                          ? "linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)"
                          : status.color === "warning"
                            ? "linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%)"
                            : "linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)",
                    border:
                      status.color === "success"
                        ? "1px solid rgba(16, 185, 129, 0.3)"
                        : status.color === "info"
                          ? "1px solid rgba(59, 130, 246, 0.3)"
                          : status.color === "warning"
                            ? "1px solid rgba(245, 158, 11, 0.3)"
                            : "1px solid rgba(239, 68, 68, 0.3)",
                    color:
                      status.color === "success"
                        ? "#059669"
                        : status.color === "info"
                          ? "#2563eb"
                          : status.color === "warning"
                            ? "#d97706"
                            : "#dc2626",
                    "& .MuiChip-icon": {
                      color:
                        status.color === "success"
                          ? "#10b981"
                          : status.color === "info"
                            ? "#3b82f6"
                            : status.color === "warning"
                              ? "#f59e0b"
                              : "#ef4444",
                    },
                  }}
                />
              )}
            </Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              sx={{
                color: "text.secondary",
                transition: "all 0.2s ease",
                "&:hover": {
                  background: "rgba(239, 68, 68, 0.1)",
                  color: "error.main",
                  transform: "scale(1.1)",
                },
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
          {/* Description */}
          {kb.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {kb.description}
            </Typography>
          )}
          {/* Progress */}
          {kb.crawlStatus === "in_progress" && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                background:
                  "linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(37, 99, 235, 0.05) 100%)",
                border: "1px solid rgba(59, 130, 246, 0.2)",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                    animation: "pulse 2s infinite",
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: "#2563eb",
                    fontSize: "0.75rem",
                  }}
                >
                  Processing Knowledge Base
                </Typography>
              </Box>
              <LinearProgress
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "rgba(59, 130, 246, 0.1)",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 4,
                    background:
                      "linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)",
                  },
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block", fontSize: "0.7rem" }}
              >
                This may take a few minutes...
              </Typography>
            </Box>
          )}
          {/* Actions */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              mt: "auto",
              pt: 2,
            }}
          >
            {(kb.crawlStatus === "queued" ||
              kb.crawlStatus === "completed") && (
              <Button
                variant="contained"
                fullWidth
                startIcon={<Edit />}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                sx={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                    boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                Edit Agent
              </Button>
            )}

            {userPlan === "enterprise" && kb.crawlStatus === "queued" && (
              <Button
                variant="contained"
                fullWidth
                startIcon={<RocketLaunch />}
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!kb.id || !kb.websiteUrl) return;

                  try {
                    setLoading(true);
                    await knowledgebaseAgentService.startCrawling(
                      kb.id,
                      kb.websiteUrl,
                    );
                    await loadKBs();
                  } catch (err) {
                    console.error(err);
                  } finally {
                    setLoading(false);
                  }
                }}
                sx={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                    boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                Start Website Crawling
              </Button>
            )}

            {userPlan === "enterprise" &&
              (kb.crawlStatus === "completed" ||
                kb.crawlStatus === "cancelled" ||
                kb.crawlStatus === "failed") && (
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<RocketLaunch />}
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!kb.id || !kb.websiteUrl) return;

                    try {
                      setLoading(true);
                      await knowledgebaseAgentService.startCrawling(
                        kb.id,
                        kb.websiteUrl,
                      );
                      await loadKBs();
                    } catch (err) {
                      console.error(err);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  sx={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                      boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  Re-Crawling
                </Button>
              )}

            {kb.crawlStatus === "completed" && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`/public/agent/${kb.id}`, "_blank");
                  }}
                  sx={{
                    flex: 1,
                    borderColor: "rgba(102, 126, 234, 0.5)",
                    color: "#667eea",
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: "#667eea",
                      background: "rgba(102, 126, 234, 0.1)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  View Agent
                </Button>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<ContentCopy />}
                  onClick={handleCopyWidgetCode}
                  sx={{
                    flex: 1,
                    borderColor: "rgba(102, 126, 234, 0.5)",
                    color: "#667eea",
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: "#667eea",
                      background: "rgba(102, 126, 234, 0.1)",
                      transform: "translateY(-1px)",
                    },
                    "&:disabled": {
                      borderColor: "rgba(0, 0, 0, 0.12)",
                      color: "rgba(0, 0, 0, 0.26)",
                    },
                  }}
                  disabled={copying}
                >
                  {copying ? "Copying..." : "Copy Code"}
                </Button>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

// ----------------- Agent Page -----------------
const Agent = () => {
  const navigate = useNavigate();

  const [kbList, setKbList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [kbToDelete, setKbToDelete] = useState(null);

  const loadKBs = async () => {
    try {
      setLoading(true);
      const result = await knowledgebaseAgentService.getAllKnowledgeBases();
      setKbList(result || []);
    } catch (err) {
      showMessage("Failed to load KB list", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadKBs();
  }, []);

  const getStatus = (kb) => {
    switch (kb.crawlStatus) {
      case "completed":
        return { label: "Ready", color: "success", icon: <CheckCircle /> };
      case "in_progress":
        return { label: "Processing", color: "info", icon: <HourglassEmpty /> };
      case "queued":
        return { label: "Queued", color: "warning", icon: <HourglassEmpty /> };
      case "failed":
        return { label: "Failed", color: "error", icon: <Delete /> };
      default:
        return { label: "Unknown", color: "default", icon: null };
    }
  };

  const confirmDeleteKB = async () => {
    if (!kbToDelete) return;
    try {
      setLoading(true);
      await knowledgebaseAgentService.deleteKnowledgeBase(kbToDelete);
      await loadKBs();
    } catch (err) {
      showMessage(err?.message || "Failed to delete KB", "error");
    } finally {
      setLoading(false);
      setDeleteConfirmOpen(false);
      setKbToDelete(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>My Agents | Sitepilot</title>
        <meta
          name="description"
          content="Manage your AI chat agents and knowledge bases"
        />
      </Helmet>
      <Container
        component="main"
        maxWidth="lg"
        sx={{
          position: "relative",
          px: { xs: 2, sm: 3 },
          py: { xs: 3, sm: 4 },
        }}
      >
        <LoaderOverlay open={loading} />

        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1.5,
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: 0 },
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  letterSpacing: "-0.02em",
                  fontSize: { xs: "2rem", md: "2.5rem" },
                }}
              >
                My Agents
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                localStorage.removeItem("onboardingState");
                navigate("/app/agent/add");
              }}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                boxShadow: "0 8px 24px rgba(102, 126, 234, 0.3)",
                borderRadius: 3,
                px: 3,
                py: 1.5,
                fontWeight: 600,
                textTransform: "none",
                fontSize: "1rem",
                whiteSpace: "nowrap",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                  boxShadow: "0 12px 32px rgba(102, 126, 234, 0.4)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              Create New Agent
            </Button>
          </Box>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ fontWeight: 400, fontSize: { xs: "1rem", md: "1.25rem" } }}
          >
            Manage your AI chat agents
          </Typography>
        </Box>

        {kbList.length === 0 ? (
          <Box sx={{ maxWidth: 600, mx: "auto", mt: 6 }}>
            <Card
              sx={{
                textAlign: "center",
                p: 6,
                border: "2px dashed rgba(102, 126, 234, 0.3)",
                borderRadius: 4,
                background:
                  "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)",
                backdropFilter: "blur(20px)",
                boxShadow: "0 20px 40px -12px rgba(0, 0, 0, 0.15)",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "rgba(102, 126, 234, 0.5)",
                  boxShadow: "0 24px 48px -12px rgba(102, 126, 234, 0.2)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                  border: "2px solid rgba(102, 126, 234, 0.2)",
                }}
              >
                <Psychology
                  sx={{
                    fontSize: 40,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                />
              </Box>
              <Typography
                variant="h5"
                sx={{
                  mb: 1,
                  fontWeight: 700,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                No Agents Yet
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Complete onboarding to create your first AI chat agent
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate("/app/agent/add")}
                sx={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  boxShadow: "0 8px 24px rgba(102, 126, 234, 0.3)",
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                    boxShadow: "0 12px 32px rgba(102, 126, 234, 0.4)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                Start Onboarding
              </Button>
            </Card>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {kbList.map((kb) => (
              <KBCard
                key={kb.id}
                kb={kb}
                status={getStatus(kb)}
                onEdit={() => navigate(`/app/agent/${kb.id}/edit`)}
                onDelete={() => {
                  setKbToDelete(kb.id);
                  setDeleteConfirmOpen(true);
                }}
                setLoading={setLoading}
                loadKBs={loadKBs}
              />
            ))}
          </Grid>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
        >
          <LoaderOverlay open={loading} />
          <DialogTitle>Delete Agent</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this agent? This action cannot be
              undone. All associated data will be permanently removed.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={confirmDeleteKB}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default Agent;
